import { useMemo } from 'react';

import { useDataTableConfigElement } from '../../../hooks/useDataTableConfig.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type { DataTableRowData } from '../../row-data-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type { DataTableOptions } from '../types/table.types.js';
import { normalizeThresholdRules } from './normalize-rules.js';
import type { DataTableThresholdProps } from './threshold-types.js';

/**
 * Hook configuring the thresholds feature for the DataTable.
 * @internal
 */
export function useThresholds<TData extends DataTableRowData>(
  props: DataTableThresholdProps<TData>,
  options: DataTableOptions<Exclude<TData, unknown>>,
): void {
  const configThresholds = useDataTableConfigElement('rowThresholds');

  // Column Thresholds depend solely on the passed column definitions,
  // so only recompute when they change instead of on every render.
  const hasColumnThresholds = useMemo(
    () =>
      options.columns.some((column) => {
        if (
          isGroupColumnDef<TDataTableFeatures, Exclude<TData, unknown>>(column)
        ) {
          return column.columns?.some(
            (childColumn) =>
              childColumn.thresholds && childColumn.thresholds.length > 0,
          );
        }
        return column.thresholds && column.thresholds.length > 0;
      }),
    [options.columns],
  );

  // check if either the table or any columns have thresholds
  const rowThresholds = props.rowThresholds ?? configThresholds;
  // First check if rowThresholds is defined, because this is the cheaper check
  if (rowThresholds) {
    options.rowThresholds = normalizeThresholdRules(rowThresholds);
    options.hasThresholds = true;
  } else {
    options.rowThresholds = undefined;
    options.hasThresholds = hasColumnThresholds;
  }
}
